// File: webelieve-frontend/src/features/notes/components/NoteIQBadge.tsx
// Intelligence badge showing extracted tasks, deadlines, entities, and privilege suggestion

import React, { useState } from 'react';

interface NoteIntelligenceSummary {
  extracted_tasks_count: number;
  extracted_deadlines_count: number;
  suggested_privilege?: string | null;
  key_entities_count: number;
  sentiment?: string | null;
  computed_at: string;
}

interface NoteIQBadgeProps {
  intelligence: NoteIntelligenceSummary | null | undefined;
  compact?: boolean;
}

const SENTIMENT_COLORS: Record<string, string> = {
  positive: 'text-green-600 dark:text-green-400',
  negative: 'text-red-600 dark:text-red-400',
  neutral: 'text-gray-500 dark:text-gray-400',
  urgent: 'text-red-600 dark:text-red-400',
  cautious: 'text-amber-600 dark:text-amber-400',
};

const NoteIQBadge: React.FC<NoteIQBadgeProps> = ({ intelligence, compact = false }) => {
  const [expanded, setExpanded] = useState(false);

  if (!intelligence) return null;

  const hasContent =
    intelligence.extracted_tasks_count > 0 ||
    intelligence.extracted_deadlines_count > 0 ||
    intelligence.key_entities_count > 0;

  if (!hasContent) return null;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-1.5">
        {intelligence.extracted_tasks_count > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600 dark:text-emerald-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            {intelligence.extracted_tasks_count}
          </span>
        )}
        {intelligence.extracted_deadlines_count > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {intelligence.extracted_deadlines_count}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900/50">
      {/* Collapsed header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700 dark:text-gray-200">NoteIQ</span>
          <div className="flex items-center gap-1.5">
            {intelligence.extracted_tasks_count > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded text-[10px]">
                {intelligence.extracted_tasks_count} task{intelligence.extracted_tasks_count > 1 ? 's' : ''}
              </span>
            )}
            {intelligence.extracted_deadlines_count > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded text-[10px]">
                {intelligence.extracted_deadlines_count} deadline{intelligence.extracted_deadlines_count > 1 ? 's' : ''}
              </span>
            )}
            {intelligence.key_entities_count > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-lex-accent-soft text-lex-accent rounded text-[10px]">
                {intelligence.key_entities_count} entit{intelligence.key_entities_count > 1 ? 'ies' : 'y'}
              </span>
            )}
          </div>
        </div>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-200 dark:border-gray-700 pt-2">
          {intelligence.suggested_privilege && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <svg className="w-3.5 h-3.5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-purple-700 dark:text-purple-300 font-medium">
                Suggested: {intelligence.suggested_privilege.replace(/-/g, ' ')}
              </span>
            </div>
          )}
          {intelligence.sentiment && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-gray-500">Tone:</span>
              <span className={`font-medium capitalize ${SENTIMENT_COLORS[intelligence.sentiment] || 'text-gray-500'}`}>
                {intelligence.sentiment}
              </span>
            </div>
          )}
          <div className="text-[10px] text-gray-400 dark:text-gray-500">
            Analyzed {new Date(intelligence.computed_at).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
};

export default NoteIQBadge;
